import { Account, AccountEmbeds, AccountTypeEnum, listAccounts } from '@kudos/http-client'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

export const allPointsQueryKey = 'ALL_MEMBER_POINTS'

const getAccountTotal = (
  account: Account | undefined,
  field: 'balance' | 'allTimeReceived' = 'balance',
): number => {
  if (!account) return 0

  return account[field] ?? 0
}

// Defaults to the current member's points if a memberId is not provided
export const useMemberPoints = (
  memberId?: string,
  embeds: AccountEmbeds[] = [],
  isEnabled: boolean = true,
) => {
  const [allocationPoints, setAllocationPoints] = useState<number>(0)
  const [redeemablePoints, setRedeemablePoints] = useState<number>(0)
  const [allTimeRedeemablePoints, setAllTimeRedeemablePoints] = useState<number>(0)
  const [arePointsLoaded, setArePointsLoaded] = useState<boolean>(false)
  const [areAllocationPointsLocked, setAreAllocationPointsLocked] = useState<boolean>(false)
  const [areRedeemablePointsLocked, setAreRedeemablePointsLocked] = useState<boolean>(false)

  const {
    data: accounts,
    isFetched: isAccountsFetched,
    refetch,
  } = useQuery({
    queryKey: [allPointsQueryKey, memberId],
    queryFn: () => listAccounts({ embeds }, memberId),
    refetchOnMount: 'always',
    enabled: isEnabled,
  })

  useEffect(() => {
    if (isAccountsFetched && accounts) {
      // Get the available points balance
      const allocationAccount = accounts.results?.find(
        (account: Account) => account.type === AccountTypeEnum.ALLOCATION,
      )

      const allocation = getAccountTotal(
        accounts.results?.find((account: Account) => account.type === AccountTypeEnum.ALLOCATION),
      )

      const redeemableAccount = accounts.results?.find(
        (account: Account) => account.type === AccountTypeEnum.REDEEMABLE,
      )

      // Get redeemable points & all time redeemed
      const redeemable = getAccountTotal(redeemableAccount)
      const allTime = getAccountTotal(redeemableAccount, 'allTimeReceived')

      setAllocationPoints(allocation)
      setRedeemablePoints(redeemable)
      setAllTimeRedeemablePoints(allTime)

      setArePointsLoaded(true)
      setAreAllocationPointsLocked(!!allocationAccount?.lockedAt)
      setAreRedeemablePointsLocked(!!redeemableAccount?.lockedAt)
    }
  }, [isAccountsFetched, accounts])

  return {
    redeemableAccount: accounts?.results?.find(
      (account: Account) => account.type === AccountTypeEnum.REDEEMABLE,
    ),
    allocationAccount: accounts?.results?.find(
      (account: Account) => account.type === AccountTypeEnum.ALLOCATION,
    ),
    allocationPoints,
    redeemablePoints,
    allTimeRedeemablePoints,
    arePointsLoaded,
    areAllocationPointsLocked,
    areRedeemablePointsLocked,
    refetch,
  }
}
